import React, { useCallback, useRef, useState } from "react";

import { useGetDashboard, useUpdateDashboard } from "api/api-hooks/dashboards-hooks";
import { Dashboard, DashboardTabOptionComponentProps, TabsDisplay } from "api/model/dashboard/Dashboard";
import { ContextTypes } from "api/model/dashboard/DashboardTab";
import CreateDatasetDialog from "components/dashboard/dataset-window/datasets-menu/create-dataset-dialog/CreateDatasetDialog";
import DatasetUsageSearchDialog from "components/fragments/dataset-usage-search/DatasetUsageSearchDialog";
import { easasSuccessAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import { warningToaster } from "components/pages/measurement-devices/utils/utils";
import { PermissionGuard } from "components/wrappers/PermissionGuard";
import useTabId from "hooks/dashboard/useTabId";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Menu } from "primereact/menu";
import { ProgressSpinner } from "primereact/progressspinner";
import { ToggleButton, ToggleButtonChangeEvent } from "primereact/togglebutton";
import { Link, useNavigate } from "react-router-dom";
import { APIError } from "services/HttpService";
import { I, T } from "services/lang/LanguageService";
import useChartDialogStore from "store/chart-dialog-store";
import useChartInDialogStore from "store/chart-in-dialog-store";
import useDashboardStore from "store/dashboard-store";
import "../dashboard-styles/DashboardMain.scss";
import { dashboardUpdateErrorHandler } from "../utils";
import DashboardConfigurator from "./DashboardConfigurator";
import DashboardEditor from "./DashboardEditor";
import DashboardGroupDropdownTabRowMenu from "./DashboardGroupDropdownTabRowMenu ";
import DashboardSubTabsDropdownMenu from "./DashboardSubTabsDropdownMenu";
import DashboardTabsDropdownMenu from "./DashboardTabsDropdownMenu";
import ImportDashboardOrTab, { handleAddNewTab } from "./ImportDashboardOrTab";

import ReorderTabsAndGroups from "./ReorderTabsAndGroups";
import DashboardEditorWarning from "./common/DashboardEditorWarning";
import AddNewTabGroup from "./tab/AddNewTabGroup";
import DashboardTabConfigurator from "./tab/DashboardTabConfigurator";
import TabContextMenu from "./tab/TabContextMenu";
import { ReorderType } from "./tab/reorder/DraggableItem";
import Reorder from "./tab/reorder/Reorder";

const TAB_DISPLAY_COMPONENTS: Partial<Record<TabsDisplay, React.FC<DashboardTabOptionComponentProps>>> = {
  [TabsDisplay.DROPDOWN]: DashboardTabsDropdownMenu,
  [TabsDisplay.DROPDOWN_ROWS]: DashboardGroupDropdownTabRowMenu,
  [TabsDisplay.SUB_TAB_DROPDOWN]: DashboardSubTabsDropdownMenu,
};

export interface ICurrentDashboard {
  dashboard: Dashboard;
  isEditMode: boolean;
  setCurrentDashboard: (dashboard: Dashboard) => void;
}

const DashboardMain = () => {
  const dashboard = useDashboardStore(state => state.dashboard);
  const isEditMode = useDashboardStore(state => state.isEditMode);
  const fromHistory = useDashboardStore(state => state.fromHistory);
  const setIsEditMode = useDashboardStore(state => state.setIsEditMode);
  const setFromHistory = useDashboardStore(state => state.setFromHistory);
  const setDashboard = useDashboardStore(state => state.setDashboard);
  const tabId = useTabId();
  const isGroupReorderEnabled = useDashboardStore(state => state.isGroupReorderEnabled);
  const setIsGroupReorderEnabled = useDashboardStore(state => state.setIsGroupReorderEnabled);

  const navigate = useNavigate();
  const { isOpen } = useChartDialogStore();
  const { isDialogVisible } = useChartInDialogStore();

  const [isImportDashboardDialogVisible, setIsImportDashboardDialogVisibility] = useState<boolean>(false);
  const [selectedTabTitle, setSelectedTabTitle] = useState<string>("");
  const [reorderTabsDialogVisibility, setReorderTabsDialogVisibility] = useState<boolean>(false);
  const [addNewGroupDialogVisibility, setAddNewGroupDialogVisibility] = useState<boolean>(false);
  const [tabConfiguratorVisibility, setTabConfiguratorVisibility] = useState<boolean>(false);
  const [dashboardConfiguratorVisibility, setDashboardConfiguratorVisibility] = useState<boolean>(false);
  const [isCreateDatasetDialogVisible, setIsCreateDatasetDialogVisible] = useState<boolean>(false);
  const [isDatasetSearchDialogVisible, setIsDatasetSearchDialogVisible] = useState<boolean>(false);

  useGetDashboard(dashboard?.id!, {
    enabled: isEditMode && !!dashboard,
    noCache: true,
    onSuccess: resp => {
      if (dashboard?.modifiedAt !== resp?.modifiedAt) {
        warningToaster(T("alert.dashboard-has-been-modified"));
        resp && setDashboard(resp);
      }
    },
  });

  const updateQuery = useUpdateDashboard({
    onSuccess: data => {
      easasSuccessAlert(T("alert.dashboard-updated"));
      setDashboard(data);
      setFromHistory(false);
    },
    onFailure: (error: APIError) => {
      dashboardUpdateErrorHandler(error, updateQuery.mutate, dashboard!);
    },
  });

  const menuRef = useRef<Menu>(null);

  const onTabChange = useCallback(
    (index: number) => {
      if (index !== +tabId) {
        navigate(`/dashboard/${dashboard?.id}/tab/${index}`);
      }
    },
    [dashboard?.id, navigate, tabId]
  );

  if (!dashboard || !dashboard.dashboardTabsJs) {
    return <ProgressSpinner />;
  }

  if (dashboard.dashboardTabsJs.length === 0) {
    handleAddNewTab(dashboard, setDashboard, T("word.tab"));
  }

  const currentTab = dashboard?.dashboardTabsJs?.[tabId];
  const currentTabContextType = currentTab?.contextConfig?.contextType || ContextTypes.NONE;

  const handleEditMode = (e: ToggleButtonChangeEvent) => {
    if (!e.value) {
      setIsGroupReorderEnabled(e.value);
    }
    setIsEditMode(e.value);
  };

  const triggerTabConfiguratorVisibility = () => {
    setSelectedTabTitle(dashboard.dashboardTabsJs[tabId].title);
    setTabConfiguratorVisibility(true);
  };

  return (
    <>
      {(isOpen || isDialogVisible) && (
        <div
          className="fixed top-0 left-0 right-0 bottom-0"
          style={{
            backgroundColor: "grey",
            opacity: 0.4,
            width: "100vw",
            height: "100vh",
            zIndex: 2,
            pointerEvents: "none",
          }}
        />
      )}
      <div className="dashboard-header mb-3">
        <div
          className={`dashboard-title${fromHistory ? " text-red-500" : ""}`}
          title={fromHistory ? T("title.history-dashboard") : ""}
        >
          {fromHistory && <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: "inherit" }} />}
          {I(dashboard?.title)}
        </div>
        {isEditMode && <DashboardEditorWarning />}
        <div className="dashboard-header gap-2 desktop-menu">
          {currentTabContextType && currentTabContextType !== ContextTypes.NONE && (
            <div className="flex">
              <TabContextMenu tabId={tabId} />
            </div>
          )}
          <PermissionGuard permissions="CUSTOMIZE_DATA_SERVICE_DASHBOARD">
            <div className="flex flex-row gap-2 edit-layout-button align-self-center">
              <ToggleButton
                iconPos="left"
                offIcon="pi pi-pencil"
                offLabel={T("action.edit-layout")}
                onIcon="pi pi-times"
                onLabel={T("action.exit-editor")}
                checked={isEditMode}
                onChange={handleEditMode}
                className="desktop-menu"
              />
            </div>
          </PermissionGuard>
        </div>
      </div>
      {isEditMode && (
        <DashboardEditor
          updateQuery={updateQuery}
          dashboard={dashboard}
          setDashboard={setDashboard}
          tabId={tabId}
          triggerTabConfiguratorVisibility={triggerTabConfiguratorVisibility}
          setReorderTabsDialogVisibility={setReorderTabsDialogVisibility}
          setDashboardConfiguratorVisibility={setDashboardConfiguratorVisibility}
          setIsImportDashboardDialogVisibility={setIsImportDashboardDialogVisibility}
          setAddNewGroupDialogVisibility={setAddNewGroupDialogVisibility}
          isGroupReorderEnabled={isGroupReorderEnabled}
          setIsGroupReorderEnabled={setIsGroupReorderEnabled}
          setIsCreateDatasetDialogVisible={setIsCreateDatasetDialogVisible}
          setDatasetSearchDialogVisibility={setIsDatasetSearchDialogVisible}
          navigate={navigate}
        />
      )}
      {dashboard.dashboardTabsJs.length > 1 && (
        <div>
          <div className="desktop-menu">
            {(dashboard.tabsDisplay ?? TabsDisplay.ROWS) === TabsDisplay.ROWS && (
              <div className="tabsFlex">
                {dashboard?.dashboardTabsJs?.map((tab, index) => (
                  <Link
                    key={tab.uuid}
                    to={`/dashboard/${dashboard.id}/tab/${index}`}
                    className={`tab dashboard-tab ${
                      tabId !== undefined && tabId === index ? "tab-selected" : "tab-unselected"
                    } ${index === 0 ? "first-tab" : ""} ${index === dashboard.dashboardTabsJs.length - 1 ? "last-tab" : ""}`}
                    data-text={I(tab.title)}
                  >
                    {I(tab.title)}
                  </Link>
                ))}
              </div>
            )}
            {dashboard.tabsDisplay &&
              TAB_DISPLAY_COMPONENTS[dashboard.tabsDisplay] &&
              React.createElement(TAB_DISPLAY_COMPONENTS[dashboard.tabsDisplay]!, {
                isMobile: false,
                menuRef,
                dashboard,
                tabId,
              })}
          </div>
          <div className="mobile-menu">
            <Button
              onClick={event => menuRef.current?.toggle(event)}
              className="p-button-icon-only mb-3 w-12 border-noround"
            >
              <i className="pi pi-bars mr-2 text-2xl" />
              <div className="font-bold text-lg">{I(dashboard?.dashboardTabsJs[tabId]?.title ?? "")}</div>
            </Button>
            <DashboardTabsDropdownMenu isMobile menuRef={menuRef} tabId={tabId} dashboard={dashboard} />
          </div>
        </div>
      )}
      {isImportDashboardDialogVisible && (
        <ImportDashboardOrTab
          isImportDashboardDialogVisible={isImportDashboardDialogVisible}
          setIsImportDashboardDialogVisibility={setIsImportDashboardDialogVisibility}
          dashboard={dashboard}
        />
      )}
      {tabConfiguratorVisibility && (
        <DashboardTabConfigurator
          onHide={() => setTabConfiguratorVisibility(false)}
          currentContextType={currentTabContextType}
          selectedTabTitle={selectedTabTitle}
          setSelectedTabTitle={setSelectedTabTitle}
          tabId={tabId}
          dashboard={dashboard}
          setDashboard={setDashboard}
        />
      )}
      {dashboardConfiguratorVisibility && (
        <DashboardConfigurator
          onHide={() => setDashboardConfiguratorVisibility(false)}
          dashboard={dashboard}
          setDashboard={setDashboard}
        />
      )}
      {addNewGroupDialogVisibility && (
        <AddNewTabGroup
          dashboard={dashboard}
          addNewGroupDialogVisibility={addNewGroupDialogVisibility}
          setAddNewGroupDialogVisibility={setAddNewGroupDialogVisibility}
          tabId={tabId}
          updateQuery={updateQuery}
        />
      )}
      {reorderTabsDialogVisibility && dashboard.tabsDisplay === TabsDisplay.ROWS && (
        <Reorder
          onChange={onTabChange}
          setReorderDialogVisibility={reorderConfig =>
            setReorderTabsDialogVisibility(reorderConfig.isVisible)
          }
          reorderDialogVisibility={reorderTabsDialogVisibility}
          reorderType={ReorderType.TABS}
        />
      )}
      {reorderTabsDialogVisibility && dashboard.tabsDisplay !== TabsDisplay.ROWS && (
        <ReorderTabsAndGroups
          onChange={onTabChange}
          isVisible={reorderTabsDialogVisibility}
          setIsVisible={setReorderTabsDialogVisibility}
          dashboard={dashboard}
          setDashboard={setDashboard}
        />
      )}
      {isCreateDatasetDialogVisible && (
        <Dialog
          header={T("action.create-dataset")}
          visible={isCreateDatasetDialogVisible}
          onHide={() => setIsCreateDatasetDialogVisible(false)}
          style={{ width: "50vw" }}
          modal={false}
        >
          <CreateDatasetDialog closeDialog={() => setIsCreateDatasetDialogVisible(false)} />
        </Dialog>
      )}
      {isDatasetSearchDialogVisible && (
        <DatasetUsageSearchDialog
          visible={isDatasetSearchDialogVisible}
          closeDialog={() => setIsDatasetSearchDialogVisible(false)}
        />
      )}
    </>
  );
};

export default DashboardMain;
